/**
 * The console side of core 2026080120 — persona authoring (#451).
 *
 * Reads the two governed read doors (`list_personas`, `persona_experience`) and
 * drives the four write verbs plus the ALM one. Every write is refused
 * server-side for a non-platform-admin — the same `core.is_platform_admin()`
 * gate `core.persona`'s `persona_admin` policy has enforced since 0030 — so this
 * module deliberately holds NO client-side authority check. A hidden button is
 * not a permission, and a UI that hides what the server would refuse teaches
 * people the wrong model of who may do what; the page surfaces the refusal
 * instead (QueryBoundary's `unauthorized` state, and a toast on a write).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSolum } from '../data/client'
import type { Row } from '@solum/sdk'

/** One row of `core.list_personas()`. */
export interface PersonaRow {
  key: string
  label: string
  sort: number
  assigned: number
  views: number
  forms: number
  cards: number
  lookups: number
  solutions: string[] | null
}

/** One row of `core.persona_experience(key)` — a surface that hangs off a persona. */
export interface PersonaSurface {
  kind: 'view' | 'form' | 'card' | 'lookup'
  entity_type: string
  name: string
  is_default: boolean
  sort_order: number
  /** The config-snapshot key — what makes this addressable as a solution component. */
  component_key: string
  in_solution: boolean
}

const KEY = ['personas'] as const

export function usePersonaList() {
  const { client } = useSolum()
  return useQuery<PersonaRow[]>({
    queryKey: [...KEY, 'list'],
    queryFn: () => client.raw().rpcTable<PersonaRow>('list_personas', {}, 'core'),
  })
}

export function usePersonaSurfaces(key: string | null) {
  const { client } = useSolum()
  return useQuery<PersonaSurface[]>({
    queryKey: [...KEY, 'surfaces', key],
    enabled: !!key,
    queryFn: () => client.raw().rpcTable<PersonaSurface>('persona_experience', { p_key: key }, 'core'),
  })
}

/** One row of `core.persona_holders()` — one person's hold on one persona. */
export interface PersonaHolder {
  /** The LOGIN id (`core.app_user.auth_user_id`) — what every assignment verb takes. */
  user_id: string
  /** The stable record identity (`core.app_user.id`), null for a login with no user record. */
  app_user_id: string | null
  display_name: string | null
  persona_key: string
  is_default: boolean
}

/** Everyone who holds a persona — `core.persona_holders` (core 2026090213).
 *
 *  This used to be `select('core','user_persona')`, the last bespoke raw table
 *  read on the identity surface (#1053 C). The door is `security invoker`, so
 *  the rows are byte for byte the ones the raw select returned — what changed
 *  is that the projection and the name join are the platform's answer rather
 *  than this file's, and an agent can ask the same question.
 *
 *  Honest limit, unchanged and still said on the page: `up_own` (core/0030)
 *  means a non-admin sees only their OWN row. It is not filtered client-side —
 *  the short list IS the permission model. */
export function usePersonaHolders() {
  const { client } = useSolum()
  return useQuery<PersonaHolder[]>({
    queryKey: [...KEY, 'assignments'],
    queryFn: () => client.raw().rpcTable<PersonaHolder>('persona_holders', {}, 'core'),
  })
}

export function useSolutions() {
  const { client } = useSolum()
  return useQuery<Row[]>({
    queryKey: [...KEY, 'solutions'],
    queryFn: () => client.raw().select<Row>('core', 'solution'),
  })
}

/** Invalidate everything the persona surface reads — assignments and roll-up
 *  counts move together, so refreshing one and not the other shows a stale
 *  number next to a fresh one. */
function useRefreshPersonas() {
  const qc = useQueryClient()
  return () => { void qc.invalidateQueries({ queryKey: KEY }) }
}

export function useAuthorPersona() {
  const { client } = useSolum()
  const refresh = useRefreshPersonas()
  return useMutation({
    // Reported per call via `mutate(vars, { onError })` — a callback on the
    // observer, which the mutation-cache net cannot see. Declared here so the
    // net does not say it a second time (#877).
    meta: { errorHandled: true },
    mutationFn: (p: { key: string; label: string; sort: number }) =>
      client.raw().rpcScalar<string>('author_persona',
        { p_key: p.key, p_label: p.label, p_sort: p.sort }, 'core'),
    onSuccess: refresh,
  })
}

export function useDeletePersona() {
  const { client } = useSolum()
  const refresh = useRefreshPersonas()
  return useMutation({
    // Reported per call via `mutate(vars, { onError })` — a callback on the
    // observer, which the mutation-cache net cannot see. Declared here so the
    // net does not say it a second time (#877).
    meta: { errorHandled: true },
    mutationFn: (key: string) => client.raw().rpcScalar<Row>('delete_persona', { p_key: key }, 'core'),
    onSuccess: refresh,
  })
}

export function useSetUserPersona() {
  const { client } = useSolum()
  const refresh = useRefreshPersonas()
  return useMutation({
    // Reported per call via `mutate(vars, { onError })` — a callback on the
    // observer, which the mutation-cache net cannot see. Declared here so the
    // net does not say it a second time (#877).
    meta: { errorHandled: true },
    mutationFn: (p: { userId: string; key: string; isDefault: boolean }) =>
      client.raw().rpcScalar<void>('set_user_persona',
        { p_user_id: p.userId, p_persona_key: p.key, p_is_default: p.isDefault }, 'core'),
    onSuccess: refresh,
  })
}

export function useRemoveUserPersona() {
  const { client } = useSolum()
  const refresh = useRefreshPersonas()
  return useMutation({
    // Reported per call via `mutate(vars, { onError })` — a callback on the
    // observer, which the mutation-cache net cannot see. Declared here so the
    // net does not say it a second time (#877).
    meta: { errorHandled: true },
    mutationFn: (p: { userId: string; key: string }) =>
      client.raw().rpcScalar<void>('remove_user_persona',
        { p_user_id: p.userId, p_persona_key: p.key }, 'core'),
    onSuccess: refresh,
  })
}

export function useAddPersonaToSolution() {
  const { client } = useSolum()
  const refresh = useRefreshPersonas()
  return useMutation({
    // Reported per call via `mutate(vars, { onError })` — a callback on the
    // observer, which the mutation-cache net cannot see. Declared here so the
    // net does not say it a second time (#877).
    meta: { errorHandled: true },
    mutationFn: (p: { solutionKey: string; personaKey: string }) =>
      client.raw().rpcScalar<{ surfaces: number }>('add_persona_to_solution',
        { p_solution_key: p.solutionKey, p_persona_key: p.personaKey }, 'core'),
    onSuccess: refresh,
  })
}

/* ── pure helpers, unit-tested ─────────────────────────────────────────────── */

/**
 * The same shape check `core.author_persona` enforces, so the field can say what
 * is wrong before a round trip. It is NOT the authority: the server refuses a
 * bad key regardless, and this returning null never means a write will succeed.
 *
 * The rule is not cosmetic. A persona key travels inside pipe-delimited config
 * and solution component keys (`entity|name|scope|persona`), so a '|' in a key
 * silently mis-addresses every export it appears in.
 */
export function personaKeyProblem(key: string): string | null {
  if (!key) return 'A persona needs a key.'
  if (!/^[a-z][a-z0-9_-]{0,63}$/.test(key)) {
    return 'Use lower-case letters, digits, hyphen or underscore, starting with a letter (max 64).'
  }
  return null
}

/** `Store lead` → `store_lead`. A suggestion while the key is untouched, never a rewrite. */
export function suggestKey(label: string): string {
  return label.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([^a-z])/, 'p$1')
    .slice(0, 64)
}

/** How many surfaces a persona actually shapes — the honest measure of whether
 *  it is doing anything (#397: ~5% of experience rows carry a persona_key). */
export function surfaceCount(p: PersonaRow): number {
  return p.views + p.forms + p.cards + p.lookups
}

/** The cascade sentence, from delete_persona's own counts. Written once so the
 *  confirmation cannot drift from what the server reports it destroyed. */
export function cascadeSummary(p: PersonaRow): string {
  const bits: string[] = []
  if (p.assigned) bits.push(`${p.assigned} ${p.assigned === 1 ? 'person' : 'people'}`)
  if (p.views) bits.push(`${p.views} ${p.views === 1 ? 'view' : 'views'}`)
  if (p.forms) bits.push(`${p.forms} ${p.forms === 1 ? 'form' : 'forms'}`)
  if (p.cards) bits.push(`${p.cards} ${p.cards === 1 ? 'card' : 'cards'}`)
  if (p.lookups) bits.push(`${p.lookups} ${p.lookups === 1 ? 'lookup' : 'lookups'}`)
  if (bits.length === 0) return 'Nothing else is attached to it.'
  const last = bits.pop()!
  return `This also deletes ${bits.length ? `${bits.join(', ')} and ${last}` : last}.`
}

/* ── navigation defaults (#511) ───────────────────────────────────────────────
 *
 * The ADMIN half of the same object a person uses to personalise their own bar.
 * Three doors, and the split is the point:
 *
 *   `navigation_defaults()` — what is CONFIGURED, for every persona and host.
 *     Not `navigation()`: that answers for the CALLER, pin first, so an admin
 *     asking "what does the finance persona see" would get their own pinned bar
 *     back and believe they were looking at somebody else's.
 *   `set_navigation(persona, doc, host)` — write one.
 *   `clear_navigation(persona, host)`   — take one off, so that persona goes
 *     back to FOLLOWING the client rather than being frozen at a copy of it.
 *
 * All three refuse a non-platform-admin server-side, and — as everywhere on this
 * page — nothing here holds a client-side authority check. A hidden button is
 * not a permission.
 */
export interface NavigationDefault {
  key: string
  persona: string | null
  host: string | null
  document: { groups: { label: string; hidden?: boolean; items: { label: string; to: string; hidden?: boolean }[] }[] }
  version: number
  updated_at: string
}

const NAV_KEY = [...KEY, 'navigation'] as const

/**
 * Every configured navigation, and — used as such by the console's in-place edit
 * mode — the PROBE for whether the caller may configure one.
 *
 * `core.navigation_defaults()` raises 42501 for a non-platform-admin rather than
 * returning nothing, deliberately: *"an editor that cannot tell 'no persona has
 * a navigation' from 'you may not look' will offer to create one and then be
 * refused on save"* (2026081012 §5). So `isSuccess` is the server's answer, not
 * a client-side role check, and nothing here holds one.
 *
 * `retry: false` because of that: a refusal is a decision, not a blip, and
 * retrying it three times writes three refusals into the audit trail and delays
 * the honest answer by as many round trips.
 */
export function useNavigationDefaults() {
  const { client } = useSolum()
  return useQuery<NavigationDefault[]>({
    queryKey: NAV_KEY,
    retry: false,
    queryFn: () => client.raw().rpcTable<NavigationDefault>('navigation_defaults', {}, 'core'),
  })
}

export function useSetPersonaNavigation() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (p: { persona: string | null; host: string | null; document: NavigationDefault['document'] | null }) => {
      if (p.document === null) {
        await client.raw().rpcScalar<boolean>('clear_navigation',
          { p_persona: p.persona, p_host: p.host }, 'core')
        return
      }
      await client.raw().rpcScalar<void>('set_navigation',
        { p_persona: p.persona, p_document: p.document, p_host: p.host }, 'core')
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NAV_KEY })
      // The admin may be editing their OWN persona's bar; the sidebar reads a
      // different key and would otherwise keep the old one until a reload.
      void qc.invalidateQueries({ queryKey: ['navigation'] })
    },
  })
}
